import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {AgGridReact} from '@ag-grid-community/react'
import {ColDef, ModuleRegistry} from '@ag-grid-community/core'
import {ExcelExportModule} from '@ag-grid-enterprise/excel-export'
import {RowGroupingModule} from '@ag-grid-enterprise/row-grouping'
import {SetFilterModule} from '@ag-grid-enterprise/set-filter'
import {StatusBarModule} from '@ag-grid-enterprise/status-bar'
import {LicenseManager} from '@ag-grid-enterprise/core'
import {Loader} from '@consta/uikit/LoaderCanary'
import moment from 'moment'

import '@ag-grid-community/styles/ag-grid.css'
import '@ag-grid-community/styles/ag-theme-alpine.css'
import '../../../component/css/agGridGraph.css'
import {API_PPK, API_PPK_ACT} from '../../../service/api.endpoint'
import api from '../api/api'
import ModalPPKView from './modal.ppk.ppkView'


ModuleRegistry.registerModules([
    ExcelExportModule,
    RowGroupingModule,
    SetFilterModule,
    StatusBarModule,
])

const license = process.env.REACT_APP_AGGRID
if (license) {
    LicenseManager.setLicenseKey(license)
}

type PpkViewMode = 'pending' | 'active' | 'archive' | 'all'

interface PpkListProps {
    isUpdate: number
    isExport: number
    viewMode: PpkViewMode
    onSummaryChange: (summary: {total: number, pending: number}) => void
}

const formatDate = (value: string | null | undefined) => (
    value ? moment(value).format('DD.MM.YYYY') : '—'
)

const GridPPKList: React.FC<PpkListProps> = ({
    isUpdate,
    isExport,
    viewMode,
    onSummaryChange,
}) => {
    const gridRef = useRef<any>()
    const [rowData, setRowData] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [graphParam, setGraphParam] = useState<any>()
    const [graphNode, setGraphNode] = useState<any>()
    const [actPath, setActPath] = useState('')

    const columnDefs = useMemo<ColDef<any>[]>(() => [
        {
            field: 'current_org_name',
            headerName: 'Текущий подрядчик',
            rowGroup: true,
            hide: true,
            enableRowGroup: true,
        },
        {
            field: 'wells.name',
            headerName: 'Скважина',
            pinned: 'left',
            width: 105,
            minWidth: 90,
            filter: 'agTextColumnFilter',
        },
        {
            field: 'deposit_name',
            headerName: 'Месторождение',
            width: 145,
            minWidth: 120,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'dns_name',
            headerName: 'ДНС',
            width: 95,
            minWidth: 80,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'wells.bush.name',
            headerName: 'Куст',
            width: 90,
            minWidth: 75,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'status_display',
            headerName: 'Статус',
            width: 155,
            minWidth: 135,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
            cellStyle: (params: any): any => params.data?.status === 'PENDING'
                ? {background: '#fff4e5', color: '#8a4b08', fontWeight: 600}
                : {background: '#eaf5e8', color: '#2f6b2f', fontWeight: 600},
        },
        {
            field: 'ppk_date',
            headerName: 'Дата акта',
            width: 110,
            minWidth: 100,
            sort: 'desc',
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: 'days_waiting',
            headerName: 'Дней',
            width: 75,
            minWidth: 68,
            type: 'numericColumn',
            cellStyle: (params: any): any => (
                params.data?.status === 'PENDING' && Number(params.value) >= 30
                    ? {color: '#b42318', fontWeight: 700}
                    : null
            ),
        },
        {
            field: 'depth',
            headerName: 'ППК, м',
            width: 90,
            minWidth: 80,
            type: 'numericColumn',
        },
        {
            field: 'ppk_commit_date',
            headerName: 'Подтверждён',
            width: 120,
            minWidth: 110,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: 'resolved_by_pass_date',
            headerName: 'Проходка 900 м',
            width: 125,
            minWidth: 115,
            hide: viewMode !== 'archive',
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: 'org_ppk.name',
            headerName: 'Организация ППК',
            width: 180,
            minWidth: 145,
            filter: 'agSetColumnFilter',
            enableRowGroup: true,
        },
        {
            field: 'comment',
            headerName: 'Примечание',
            minWidth: 220,
            flex: 1,
            valueFormatter: (params: any) => params.value || '—',
        },
        {
            field: 'created_at',
            headerName: 'Добавлен',
            hide: true,
            valueFormatter: (params: any) => (
                params.value ? moment(params.value).add(5, 'hour').format('DD.MM.YYYY HH:mm') : '—'
            ),
        },
        {
            field: 'updated_at',
            headerName: 'Обновлён',
            hide: true,
            valueFormatter: (params: any) => (
                params.value ? moment(params.value).add(5, 'hour').format('DD.MM.YYYY HH:mm') : '—'
            ),
        },
    ], [viewMode])

    const defaultColDef = useMemo<ColDef<any>>(() => ({
        sortable: true,
        resizable: true,
        suppressHeaderContextMenu: true,
    }), [])

    const autoGroupColumnDef = useMemo<ColDef<any>>(() => ({
        headerName: 'Подрядчик',
        pinned: 'left',
        minWidth: 235,
        width: 260,
        cellRendererParams: {suppressCount: false},
    }), [])

    const statusBar = useMemo(() => ({
        statusPanels: [
            {statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left'},
        ],
    }), [])

    const fetchData = useCallback(async () => {
        setIsLoading(true)
        try {
            const response = await api.get(API_PPK, {
                params: {view: viewMode},
            })
            const rows = Array.isArray(response.data) ? response.data : []
            setRowData(rows)
            onSummaryChange({
                total: rows.length,
                pending: rows.filter((item: any) => item.status === 'PENDING').length,
            })
        } catch (error) {
            console.error('Не удалось загрузить ППК:', error)
            setRowData([])
            onSummaryChange({total: 0, pending: 0})
        } finally {
            setIsLoading(false)
        }
    }, [onSummaryChange, viewMode])

    useEffect(() => {
        fetchData()
    }, [fetchData, isUpdate])

    useEffect(() => {
        if (isExport && gridRef.current?.api) {
            gridRef.current.api.exportDataAsExcel({
                fileName: `ppk_${moment().format('YYYY-MM-DD')}.xlsx`,
            })
        }
    }, [isExport])

    const openRecord = async (params: any) => {
        if (!params.data) return
        try {
            const response = await api.get(API_PPK_ACT, {
                params: {ppk_id: params.data.pk},
            })
            setActPath(response.data?.act || '')
        } catch (error) {
            console.error('Не удалось получить акт ППК:', error)
            setActPath(params.data.act || '')
        }
        setGraphParam({collName: params.colDef.field})
        setGraphNode(params.node)
        setIsModalOpen(true)
    }

    const handleModalClose = async () => {
        setIsModalOpen(false)
        await fetchData()
    }

    return (
        <div className="ppk-grid-wrapper">
            <div className="ag-theme-alpine ppk-grid">
                {isLoading ? (
                    <div className="ppk-grid-loader">
                        <Loader/>
                    </div>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        autoGroupColumnDef={autoGroupColumnDef}
                        groupDisplayType="singleColumn"
                        groupDefaultExpanded={0}
                        rowGroupPanelShow="always"
                        animateRows
                        getRowId={(params: any) => params.data.pk}
                        statusBar={statusBar}
                        onCellDoubleClicked={openRecord}
                    />
                )}
            </div>
            <ModalPPKView
                isOpen={isModalOpen}
                onClose={handleModalClose}
                props={graphParam}
                nodeData={graphNode}
                actPath={actPath}
            />
        </div>
    )
}

export default GridPPKList
